// Standard storefront events (Shopify, June 2026).
//
// A theme dispatches these when a buyer does something, so apps, pixels and agents can react to the
// interaction instead of reading our markup or watching our network calls - and we get the same
// payload shape every other theme sends. The library ships a class per event; this theme uses plain
// script tags rather than an import map, so it takes the documented global assignment.
//
// Deliberately NOT dispatched here: cart:lines-update for changes an app made through
// Shopify.actions.updateCart. That action emits its own cart events, and dispatching after it would
// make listeners see the same change twice. Our calls cover the changes this theme makes itself.
import * as SE from 'https://cdn.shopify.com/storefront/standard-events.js';

window.StandardEvents = SE;

const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const templateName = () =>
  (window.Shopify && window.Shopify.template) ||
  document.documentElement.getAttribute('data-ms-template') ||
  '';

const cartTarget = () => document.querySelector('[data-ms-cart-drawer]') || document;

const toStandardCart = (ajaxCart) => {
  if (!ajaxCart) return null;
  if (SE.CartLinesUpdateEvent && SE.CartLinesUpdateEvent.createCartFromAjaxResponse) {
    return SE.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);
  }
  return null;
};

// shopify:page:view - fires once per page load, from document, inside a DOMContentLoaded listener so
// app scripts have time to attach their listeners first.
const dispatchPageView = () => {
  if (!SE.PageViewEvent) return;
  document.dispatchEvent(
    new SE.PageViewEvent({
      page: { template: templateName(), title: document.title, url: window.location.href },
    })
  );
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', dispatchPageView);
else dispatchPageView();

// shopify:cart:lines-update (+ shopify:cart:error on failure).
// The event fires as the operation starts and carries a promise, which is what lets a listener show a
// loading state in between. `run` is the theme's own cart call.
const cartLinesUpdate = (element, { action, context, lines, run }) => {
  const target = element || cartTarget();
  if (!SE.CartLinesUpdateEvent) return run();
  const deferred = SE.CartLinesUpdateEvent.createPromise();
  // a rejected event promise nobody listened to surfaces as an unhandled rejection, which the browser
  // logs and Node treats as fatal; this keeps the original promise intact for real listeners
  deferred.promise.catch(() => {});
  target.dispatchEvent(new SE.CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
  return run()
    .then(async (result) => {
      const ajaxCart = (result && result.cart) || (await fetch(`${routesRoot()}cart.js`).then((r) => r.json()));
      deferred.resolve({ cart: toStandardCart(ajaxCart) });
      return result;
    })
    .catch((error) => {
      deferred.reject(error);
      if (SE.CartErrorEvent) {
        target.dispatchEvent(
          new SE.CartErrorEvent({ error: String((error && error.message) || error), code: 'SERVICE_UNAVAILABLE' })
        );
      }
      throw error;
    });
};

// shopify:cart:view - the buyer opened the cart, as a drawer here.
const cartView = async (context = 'dialog') => {
  if (!SE.CartViewEvent) return;
  let cart = null;
  try {
    cart = toStandardCart(await fetch(`${routesRoot()}cart.js`).then((r) => r.json()));
  } catch (error) {
    /* a view event without a cart payload is still better than none */
  }
  cartTarget().dispatchEvent(new SE.CartViewEvent({ context, cart }));
};

// shopify:product:select - the buyer changed an option, before anything is added.
const productSelect = (element, { product, selectedOptions, variant }) => {
  if (!SE.ProductSelectEvent || !element) return;
  const deferred = SE.ProductSelectEvent.createPromise();
  deferred.promise.catch(() => {});
  element.dispatchEvent(new SE.ProductSelectEvent({ product, selectedOptions, promise: deferred.promise }));
  deferred.resolve({ variant: variant || null });
};

// shopify:collection:update - the buyer changed a filter or the sort order. The promise resolves with
// the number of products that matched, which our client-side layer only knows once it has loaded the
// match set, so the event is dispatched at change time and settled when the grid is final.
let collectionDeferred = null;
const collectionUpdate = (form, { collection, productFilters, sortKey }) => {
  if (!SE.CollectionUpdateEvent || !form) return;
  if (collectionDeferred) {
    try {
      collectionDeferred.resolve({ productsCount: null });
    } catch (error) {
      /* already settled */
    }
  }
  const deferred = SE.CollectionUpdateEvent.createPromise();
  deferred.promise.catch(() => {});
  collectionDeferred = deferred;
  form.dispatchEvent(
    new SE.CollectionUpdateEvent({
      collection,
      productFilters: productFilters && productFilters.length ? productFilters : undefined,
      sortKey: sortKey || undefined,
      promise: deferred.promise,
    })
  );
  // the grid reloads with the new filters, so nothing may settle it: resolve defensively
  window.setTimeout(() => {
    if (collectionDeferred === deferred) {
      try {
        deferred.resolve({ productsCount: null });
      } catch (error) {
        /* already settled */
      }
      collectionDeferred = null;
    }
  }, 2000);
};
const collectionCount = (count) => {
  if (!collectionDeferred) return;
  const deferred = collectionDeferred;
  collectionDeferred = null;
  try {
    deferred.resolve({ productsCount: count });
  } catch (error) {
    /* already settled */
  }
};

window.MStoreStandardEvents = {
  cartLinesUpdate,
  cartView,
  productSelect,
  collectionUpdate,
  collectionCount,
  toStandardCart,
};
